import {
  Home as HomeIcon,
  Briefcase,
  Building2,
  ShoppingBag,
  Store,
  HeartPulse,
  Siren,
  Wallet,
  Check,
} from "lucide-react";

const features = [
  { icon: HomeIcon, title: "Local Needs", desc: "Post everyday requirements and connect instantly with nearby people ready to help." },
  { icon: Briefcase, title: "Part-Time Jobs", desc: "Discover flexible work opportunities that fit your schedule." },
  { icon: Building2, title: "Full-Time Jobs", desc: "Find career opportunities from trusted local businesses" },
  { icon: ShoppingBag, title: "Marketplace", desc: "Buy, sell, or rent products directly with your local community." },
  { icon: Store, title: "Local Shops", desc: "Explore nearby businesses, services, and trusted local stores." },
  { icon: HeartPulse, title: "Emergency Blood", desc: "Request or donate blood quickly during emergencies and help save lives." },
  { icon: Siren, title: "SOS Safety", desc: "Share your live location with trusted contacts when you need immediate assistance." },
  { icon: Wallet, title: "Wallet & Credits", desc: "Manage credits seamlessly for posting tasks, unlocking opportunities, and accessing premium features" },
];

const whyItems = [
  "Local-first experience",
  "Direct communication",
  "Transparent credits",
  "Verified businesses",
  "Multiple services in one app",
  "Designed for everyday city life",
];

export function WhatIs() {
  return (
    <section id="about-app" className="py-20 md:py-28">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF3ED] text-[#FF5A00] px-4 py-1.5 text-xs font-semibold">
            What is Time2Cash
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#1A1918] leading-tight">
            Your City&apos;s <span className="brand-gradient-text">Digital Community.</span>
          </h2>
          <p className="mt-5 text-lg text-[#4A4846] leading-relaxed">
            Time2Cash is an all-in-one local platform that connects people, businesses, jobs, services, marketplaces, and community support in one app. Instead of switching between multiple applications, users can find opportunities, hire local professionals, discover nearby shops, buy or sell products, request emergency blood, and access essential local services—all from a single trusted platform.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { n: "8+", l: "Integrated Modules" },
              { n: "1", l: "Super App" },
              { n: "0", l: "Middlemen" },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-white border border-[#EBE8E3] p-4">
                <div className="text-3xl font-extrabold text-[#FF5A00]">{s.n}</div>
                <div className="text-xs text-[#78716C] mt-1 font-medium">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 bg-[#FF5A00]/10 rounded-[2rem] rotate-3" />
          <img
            src="/Media-Library/h1.png"
            alt="Local community"
            className="relative rounded-[2rem] w-full aspect-[4/5] object-cover shadow-xl"
          />
        </div>
      </div>
    </section>
  );
}

export function EverythingYouNeed() {
  return (
    <section id="features" className="py-20 md:py-28 bg-[#FFF9F5]">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EBE8E3] px-4 py-1.5 text-xs font-semibold text-[#4A4846]">
            Everything you need
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#1A1918] leading-tight">
            One App. <span className="brand-gradient-text">Every Local Need.</span>
          </h2>
          <p className="mt-4 text-lg text-[#4A4846]">
            Eight powerful modules designed to connect people, businesses, opportunities, and essential services—bringing your entire city into one seamless experience.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="group bg-white rounded-3xl border border-[#EBE8E3] p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-2xl bg-[#FFF3ED] flex items-center justify-center group-hover:bg-[#FF5A00] transition-colors">
                <Icon className="w-6 h-6 text-[#FF5A00] group-hover:text-white transition-colors" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-[#1A1918]">{title}</h3>
              <p className="mt-2 text-sm text-[#4A4846] leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function WhyTime2Cash() {
  return (
    <section className="py-20 md:py-28">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF3ED] text-[#FF5A00] px-4 py-1.5 text-xs font-semibold">
              Why Time2Cash
            </div>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#1A1918] leading-tight">
              Six reasons your <br /> neighbourhood loves it.
            </h2>
            <p className="mt-5 text-lg text-[#4A4846]">
              Built ground-up for real Indian cities, real streets, real conversations.
            </p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {whyItems.map((w) => (
              <li key={w} className="flex items-start gap-3 rounded-2xl bg-white border border-[#EBE8E3] p-4">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="text-[#1A1918] font-semibold">{w}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
